import {UtilService} from './../../../services/util.service';
import {Component, DestroyRef, inject} from '@angular/core';
import {AlertsService} from '../../../services/alerts.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AlertFrequencyMap, AlertMetricMap, AlertType, AlertTypeMap, GetAlertSubscriptionResponse} from '../types';
import {ActivatedRoute, Router} from '@angular/router';
import {HighchartsChartModule} from 'highcharts-angular';
import {LoaderComponent} from '../../../shared/components/loader/loader.component';
import Highcharts from 'highcharts';
import {MY_BRAND_COLOR, PolarisRoutePaths, RoutePaths} from '../../../shared/types/common';
import {CommonModule} from '@angular/common';
import {filter, map, switchMap, tap} from 'rxjs';
import {POLARIS_NAME} from '../../copilot/copilot.model';
import {CopilotPromptBarComponent} from '../../copilot/components/copilot-prompt-bar/copilot-prompt-bar.component';
import {FormControl} from '@angular/forms';
import {AlertPayload} from '../../copilot/types';
import {DashboardService} from '../../../services/dashboard.service';
import {PosthogService} from '../../../services/posthog.service';
import {PosthogEventCode, PosthogEventName} from '../../../services/types';
import {PolarisRuntimeFacadeService} from '../../../services/polaris/polaris-runtime-facade.service';
import {PolarisDataDomain, PolarisStartChatOptionsVm} from '../../../services/polaris/model';
import {AssetVersionPipe} from '../../../pipes/asset-version.pipe';

@Component({
  selector: 'app-alert-view',
  imports: [
    HighchartsChartModule,
    LoaderComponent,
    CommonModule,
    CopilotPromptBarComponent,
    AssetVersionPipe
  ],
  templateUrl: './alert-view.component.html',
  styleUrl: './alert-view.component.scss'
})
export class AlertViewComponent {

  private destroyRef = inject(DestroyRef);
  private alertsService = inject(AlertsService);
  protected utilService = inject(UtilService);
  protected route = inject(ActivatedRoute);
  private router = inject(Router);
  private dashboardService = inject(DashboardService);
  private posthogService = inject(PosthogService);
  private polarisRuntimeFacadeService = inject(PolarisRuntimeFacadeService);

  protected AlertTypeMap = AlertTypeMap;
  protected AlertFrequencyMap = AlertFrequencyMap;
  protected AlertMetricMap = AlertMetricMap;

  protected loading: boolean = false;
  protected alert: GetAlertSubscriptionResponse | null = null;
  protected alertDescription: string | null = null;
  protected hasAlertCenterPermissions: boolean = false;
  protected promptControl = new FormControl('');
  protected readonly promptBarPlaceholder = 'Analyze this alert for me';

  protected chartContainerId: string = 'performance-over-time';

  ngOnInit(): void {
    this.hasAlertCenterPermissions = this.alertsService.hasPermissions();
    if (!this.hasAlertCenterPermissions) {
      return;
    } else {
      this.getAlertSubscription();
    }
  }

  protected openAlertCenter(): void {
    if (this.hasAlertCenterPermissions) {
      this.router.navigate([RoutePaths.ALERT_CENTER]);
    }
  }

  protected onSendMessage(message: string): void {
    const brandKey = this.alert?.brandKey;
    if (!brandKey) {
      return;
    }
    const competitorKeys =
      this.dashboardService.brandsOptions().brandCompetitorsResponse?.[brandKey]?.competitors?.map(
        c => c.key
      ) ?? [];

    const startChatOptions: PolarisStartChatOptionsVm = {
      origin: 'alert_view_page',
      modelVersion: 'v2',
      dataDomain: PolarisDataDomain.OPEN_WEB
    };
    const alertPayload = this.buildAlertPayload();
    if (alertPayload) {
      startChatOptions.alert = alertPayload;
    }

    this.polarisRuntimeFacadeService.setUserBrandContext(brandKey, competitorKeys);
    this.posthogService.capture(PosthogEventName.POLARIS_PROMPT_SENT, {
      [PosthogEventCode.MY_BRAND]: brandKey,
      [PosthogEventCode.MY_COMPETITORS]: competitorKeys.join(';'),
      [PosthogEventCode.ORIGIN]: startChatOptions.origin,
      [PosthogEventCode.PROMPT_CONTENT]: message
    });
    void this.startChatAndNavigate(message, startChatOptions);
  }

  private async startChatAndNavigate(message: string, options: PolarisStartChatOptionsVm): Promise<void> {
    try {
      const chatId = await this.polarisRuntimeFacadeService.startChat(message, options);
      await this.router.navigate([`${RoutePaths.POLARIS}/${PolarisRoutePaths.CHAT}/${chatId}`]);
    } catch {
      // Stay on alert view when chat creation fails.
    }
  }

  private buildAlertPayload(): AlertPayload | undefined {
    if (!this.alert) {
      return undefined;
    }
    return {
      alert_type: this.alert?.alertType as string,
      metric: this.alert?.metric as string,
      change: {
        direction: this.alert?.alertType as string,
        percent: `${this.alert?.percentChange * 100}%`
      },
      date_before: this.alert?.dateBefore as string,
      date_after: this.alert?.dateAfter as string,
      brand_key: this.alert?.brandKey as string
    }
  }

  private getAlertSubscription(): void {
    this.route.params.pipe(
      takeUntilDestroyed(this.destroyRef),
      map(params => params['id']),
      filter((alertId): alertId is string => !!alertId),
      tap(() => this.loading = true),
      switchMap(alertId => this.alertsService.getAlertSubscription(alertId))
    )
      .subscribe({
        next: response => {
          this.handleAlertResponse(response);
          this.loading = false;
        },
        error: error => {
          console.error('Error fetching alert subscription:', error);
          this.loading = false;
        }
      });
  }

  private handleAlertResponse(response: GetAlertSubscriptionResponse): void {
    this.alert = response;
    this.alertDescription = this.generateAlertDescription();
    this.renderChart();
  }

  private generateAlertDescription(): string | null {
    if (this.alert) {
      const brand = this.utilService.capitalizeFirstLetter(this.alert.brand);
      const format = this.AlertMetricMap[this.alert.metric].formatMetric;
      const percentChange = this.utilService.convertToPercentage(this.alert.percentChange);
      const valueBefore = this.utilService.getDisplayMetric(this.alert.valueBefore, format, true);
      const valueAfter = this.utilService.getDisplayMetric(this.alert.valueAfter, format, true);
      if (this.alert.alertType === AlertType.SPIKE) {
        return `
          ${brand}'s ${this.alert.metric} showed a significant jump of ${percentChange}, increasing from ${valueBefore} to ${valueAfter}.
          Ask ${POLARIS_NAME} to analyze what’s behind this change or answer any follow-up questions.
        `;
      } else if (this.alert.alertType === AlertType.DROP) {
        return `
          ${brand}'s ${this.alert.metric} showed a notable drop of ${percentChange}, decreasing from ${valueBefore} to ${valueAfter}.
          Ask ${POLARIS_NAME} to analyze what’s behind this change or answer any follow-up questions.
        `;
      }
    }
    return null;
  }

  private renderChart(): void {
    if (!this.alert) {
      return;
    }

    const textStyle = {
      fontFamily: 'Poppins',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '18px',
      color: '#686D75'
    }
    const format = this.AlertMetricMap[this.alert.metric].formatMetric;
    const categories = this.alert.trendSeries.map(dataPoint => dataPoint.date);
    const dataSeries = this.alert.trendSeries.map(dataPoint => dataPoint.value);
    const allSeries: Highcharts.SeriesSplineOptions[] = [
      {
        name: this.utilService.capitalizeFirstLetter(this.alert.brand),
        data: dataSeries,
        type: 'spline',
        marker: {symbol: 'circle'},
        color: MY_BRAND_COLOR
      }
    ];

    const chartOptions: Highcharts.Options = {
      chart: {
        type: 'spline',
        spacingBottom: -5,
        animation: false,
      },
      title: {
        text: this.AlertMetricMap[this.alert.metric].name,
        align: 'left',
        margin: 50,
        style: {
          fontFamily: 'Poppins',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: '600',
          lineHeight: '24px',
          height: 64,
        }
      },
      credits: {
        enabled: false
      },
      xAxis: {
        categories: categories as string[],
        lineColor: 'transparent',
        title: {
          text: null
        },
        labels: {
          style: textStyle,
          rotation: -90,
          formatter: (function (utilService) {
            return function () {
              return utilService.formatDateToShort(String(this.value));
            }
          })(this.utilService)
        }
      },
      yAxis: {
        title: {
          text: null
        },
        labels: {
          style: textStyle,
          formatter: (function (utilService, format) {
            return function () {
              return utilService.getDisplayMetric(Number(this.value), format, true);
            }
          })(this.utilService, format)
        },
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false,
            symbol: 'circle',
            states: {
              hover: {
                enabled: true,
                width: 12,
                height: 12,
              }
            },
          },
          showInLegend: true
        }
      },
      legend: {
        useHTML: true,
        symbolWidth: 0,
        align: 'left',
        alignColumns: true,
        itemStyle: textStyle,
        events: {
          itemClick: () => false,
        },
        itemHoverStyle: {
          enabled: false,
          cursor: 'default'
        },
        labelFormatter: function () {
          return `
            <span style="display: inline-block;
                        width: 10px; height: 10px;
                        background-color: ${this.color};
                        border-radius: 50%;
                        margin-right: 3px;">
            </span> ${this.name}
          `;
        }
      },
      tooltip: {
        shared: true,
        useHTML: true,
        shadow: false,
        backgroundColor: 'transparent',
        formatter: (function (utilService, format) {
          return function () {
            let content = `
              <div style="
                display: flex; flex-direction: column;
                padding: 12px; gap: 6px;
                color: #34363A; background: #FFFFFF;
                font-weight: 400; font-style: normal; font-family: Poppins, sans-serif;
                font-size: 12px;
                border-radius: 4px; box-shadow: 0px 0px 8px 2px rgba(0, 0, 0, 0.15);
              ">
              <b>${utilService.formatDateToShort(String(this.category))}</b>
            `;
            const chart = this.points?.[0]?.series?.chart;
            (chart?.series || []).forEach((series, index) => {
              if (!series.visible) return;
              const value = series.data.find(p => p.x === this.x)?.y;
              const displayValue = value ? utilService.getDisplayMetric(value, format, true) : 'N/A';
              content += `
                <div>
                  <span style="color:${series.color};font-size:16px;">\u25CF</span> ${series.name} <b>${displayValue}</b>
                </div>
              `;
            });
            content += `</div>`;
            return content;
          }
        })(this.utilService, format),
      },
      series: allSeries
    };

    Highcharts.chart(this.chartContainerId, chartOptions);
  }

  protected onLogoError(event: Event): void {
    (event.target as HTMLImageElement).src = 'images/no-logo.png';
  }

}
